import { Volume2, VolumeX } from 'lucide-react';
import { useState } from 'react';

import { useTranslation } from '@/components/i18n/Translator';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

interface Props {
  content: unknown;
  className?: string;
  messageId?: string;
}

const SpeakerButton = ({ content, className, messageId }: Props) => {
  const [reading, setReading] = useState(false);
  const { t } = useTranslation();

  /**
   * Clean text for TTS by removing URLs, markdown syntax, and superscripts
   * while preserving the original content display
   */
  const cleanTextForTTS = (text: string): string => {
    let cleaned = text;

    // Remove URLs (http, https, ftp, www)
    cleaned = cleaned.replace(/https?:\/\/[^\s)]+/g, '');
    cleaned = cleaned.replace(/www\.[^\s)]+/g, '');
    cleaned = cleaned.replace(/ftp:\/\/[^\s)]+/g, '');

    // Remove Markdown Headers (e.g. # Header) and hashtags
    cleaned = cleaned.replace(/#/g, '');

    // Remove Markdown Bold/Italic/Code/Strikethrough markers (*, _, `, ~)
    cleaned = cleaned.replace(/[\*_`~]/g, '');
    
    // Remove superscript markers and content
    // Matches [1], [^1], [2], etc.
    cleaned = cleaned.replace(/\[\^?\d+\]/g, '');
    // Matches ^text, ^(text), ^2
    cleaned = cleaned.replace(/\^(\w+|\([^\)]+\))/g, '');
    // Remove HTML tags
    cleaned = cleaned.replace(/<[^>]*>/g, '');
    // Remove remaining carets
    cleaned = cleaned.replace(/\^/g, '');

    // Remove Unicode superscripts (¹ ² ³ ⁴ ⁵ ⁶ ⁷ ⁸ ⁹ ⁰ ⁺ ⁻ ⁼ ⁽ ⁾ ⁿ ⁱ)
    cleaned = cleaned.replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹⁺⁻⁼⁽⁾ⁿⁱ]/g, '');
    
    // Remove markdown link syntax but keep the text [text](url) -> text
    cleaned = cleaned.replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1');
    
    // Clean up extra whitespace
    cleaned = cleaned.replace(/\s+/g, ' ').trim();
    
    return cleaned;
  };

  const toggleReading = async () => {
    try {
      if (reading) {
        // Stop reading/speaking
        if ('speechSynthesis' in window) {
          window.speechSynthesis.cancel();
        }
        setReading(false);
      } else {
        // Start reading/speaking
        let textToSpeak = typeof content === 'string' ? content : JSON.stringify(content);
        
        // Clean the text before sending to TTS
        textToSpeak = cleanTextForTTS(textToSpeak);
        
        if ('speechSynthesis' in window && textToSpeak) {
          const utterance = new SpeechSynthesisUtterance(textToSpeak);
          
          utterance.onstart = () => {
            setReading(true);
          };
          
          utterance.onend = () => {
            setReading(false);
          };
          
          utterance.onerror = () => {
            setReading(false);
          };
          
          window.speechSynthesis.speak(utterance);
        }
      }
    } catch (error) {
      console.error('Speech synthesis error:', error);
      setReading(false);
    }
  };

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={toggleReading}
            variant="ghost"
            size="icon"
            className={`text-muted-foreground ${className}`}
          >
            {reading ? (
              <VolumeX className="h-4 w-4" />
            ) : (
              <Volume2 className="h-4 w-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {reading
              ? 'Stop reading'
              : 'Read aloud'}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default SpeakerButton;